import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ExpertSlider from "./ExpertSlider";
import { getTherapist, clearTherapistDetail } from "../../redux/therapistSlice";
import { getPackages } from "../../redux/packagesSlice";
import { getLanguages } from "../../redux/languageSlice";
import { createPaymentIntent, clearPaymentIntent, clearPaymentError } from "../../redux/paymentSlice";
import { USER_TIMEZONE } from "../../utils/dateUtils";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import axios from "axios";

const API_URL = process.env.REACT_APP_API_URL;

const TherapistDetail = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [selectedSession, setSelectedSession] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date().getMonth());
  const [currentYear, setCurrentYear] = useState(new Date().getFullYear());
  const [sessionType, setSessionType] = useState("individual");
  const [couponCode, setCouponCode] = useState("");
  const [coupon, setCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const navigate = useNavigate();

  // Get therapist information from Redux state - DETAY SAYFASI İÇİN
  const therapistsState = useSelector((state) => state.therapist);
  const therapist = therapistsState?.therapistDetail; // therapist yerine therapistDetail kullan
  const loading = therapistsState?.loading || false;
  const error = therapistsState?.error || null;

  // Get package information
  const packagesState = useSelector((state) => state.packages);
  const packages = packagesState?.packages || [];

  // Get languages information
  const languageState = useSelector((state) => state.languages);
  const fetchedLanguages = languageState?.fetchedLanguages || [];

  // Get payment information
  const paymentState = useSelector((state) => state.payment);
  const paymentIntent = paymentState?.paymentIntent || null;
  const paymentLoading = paymentState?.loading || false;
  const paymentError = paymentState?.error || null;

  // Session packages - coming from API
  const sessionPackages = packages.filter(pkg => pkg.isActive) || [];
  
  // Check user authentication and type from localStorage
  const checkUserAuth = () => {
    try {
      const userToken = localStorage.getItem('token');
      const userType = localStorage.getItem('userType');
      
      if (!userToken) {
        return { authenticated: false, userType: null };
      }
      
      return { authenticated: true, userType: userType };
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return { authenticated: false, userType: null };
    }
  };
  
  // Component unmount olduğunda detay bilgilerini temizle
  useEffect(() => {
    return () => {
      dispatch(clearTherapistDetail());
    };
  }, [dispatch]);

  // API requests
  useEffect(() => {
    if (id) {
      dispatch(getTherapist(id));
      dispatch(getPackages());
      dispatch(getLanguages());
    }
  }, [dispatch, id]);

  // Redirect when payment intent is successful
  useEffect(() => {
    if (!paymentIntent) {
      return;
    }

    // Free session handling - no Stripe redirect needed
    if (paymentIntent.isFreeSession) {
      toast.success(t("freeSessionBooked"), {
        position: "top-center",
        autoClose: 3000,
      });
      dispatch(clearPaymentIntent());
      setTimeout(() => {
        navigate("/userTherapyPlanner");
      }, 1500);
      return;
    }

    const extractUrl = (intent) => {
      if (!intent) return null;
      const directUrl = intent.paymentUrl || intent.checkoutUrl || intent.url;
      if (directUrl) return directUrl;
      const nested = intent.data || {};
      return nested.paymentUrl || nested.checkoutUrl || nested.url || null;
    };

    const paymentUrl = extractUrl(paymentIntent);
    const successFlag = (() => {
      if (typeof paymentIntent.success === "boolean") return paymentIntent.success;
      if (typeof paymentIntent.status === "string") return paymentIntent.status.toLowerCase() === "success";
      if (typeof paymentIntent.data?.success === "boolean") return paymentIntent.data.success;
      return true;
    })();

    if (paymentUrl && successFlag) {
      window.location.href = paymentUrl;
      dispatch(clearPaymentIntent());
    }
  }, [paymentIntent, dispatch, navigate, t]);

  useEffect(() => {
    if (paymentError) {
      const errorMessage =
        typeof paymentError === "string"
          ? paymentError
          : paymentError.message || paymentError.error || "Payment could not be initiated.";

      toast.error(errorMessage, {
        position: "top-center",
        autoClose: 3000,
      });

      dispatch(clearPaymentError());
    }
  }, [paymentError, dispatch]);

  // Get available times for selected date - Fixed UTC issue
  const getAvailableTimesForDate = (selectedDate) => {
    if (!therapist || !therapist.availabilities || !selectedDate) {
      return [];
    }

    // Seçilen tarihin bilgilerini al
    const selectedYear = selectedDate.getFullYear();
    const selectedMonth = selectedDate.getMonth();
    const selectedDay = selectedDate.getDate();

    return therapist.availabilities
      .filter((availability) => {
        const availabilityDate = new Date(availability.startDate);
        // Backend'den gelen tarihleri olduğu gibi karşılaştır
        return availabilityDate.getFullYear() === selectedYear &&
              availabilityDate.getMonth() === selectedMonth &&
              availabilityDate.getDate() === selectedDay &&
              availability.status === 'available';
      })
      .map((availability) => {
        const startTime = new Date(availability.startDate);
        return {
          time: `${startTime.getHours().toString().padStart(2, '0')}:${startTime.getMinutes().toString().padStart(2, '0')}`,
          availability: availability
        };
      })
      .sort((a, b) => a.time.localeCompare(b.time));
  };

  // Check if there's availability on date - Fixed UTC issue
  const hasAvailabilityOnDate = (date) => {
    if (!therapist || !therapist.availabilities) {
      return false;
    }

    const dateYear = date.getFullYear();
    const dateMonth = date.getMonth();
    const dateDay = date.getDate();

    return therapist.availabilities.some((availability) => {
      const availabilityDate = new Date(availability.startDate);
      return availabilityDate.getFullYear() === dateYear &&
            availabilityDate.getMonth() === dateMonth &&
            availabilityDate.getDate() === dateDay &&
            availability.status === 'available';
    });
  };

  // Month names
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  // Day names
  const dayNames = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

  // Generate calendar days
  const generateCalendarDays = () => {
    const firstDay = new Date(currentYear, currentMonth, 1);
    const startDate = new Date(firstDay);
    // getDay(): 0=Sun,1=Mon,...,6=Sat — takvim Pazartesi başladığı için offset düzeltmesi
    const dayOfWeek = firstDay.getDay();
    const daysToSubtract = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
    startDate.setDate(startDate.getDate() - daysToSubtract);

    const days = [];
    let currentDate = new Date(startDate);

    // Show 6x7=42 days
    for (let i = 0; i < 42; i++) {
      days.push(new Date(currentDate));
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  // Month navigation functions
  const previousMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const nextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  // Session selection function
  const handleSessionSelect = (session) => {
    setSelectedSession(session._id);
    // If quantity is more than 1, reset date and time selection
    if (parseInt(session.quantity) > 1) {
      setSelectedDate(null);
      setSelectedTime(null);
    }
  };

  const getSessionPrice = (quantity) => {
    if (!therapist) return 0;
    const basePrice = sessionType === "couples"
      ? (therapist.coupleSessionPrice || therapist.hourlyPrice || 0)
      : (therapist.hourlyPrice || 0);
    return quantity * basePrice;
  };

  // Helper to format package name if it's "string"
  const getPackageName = (session) => {
    if (session.name !== 'string') return session.name;
    
    const quantity = parseInt(session.quantity) || 1;
    const type = session.appType || 'video';
    
    // Translation keys: single_video_session, multiple_video_sessions, etc.
    const key = quantity === 1 ? `single_${type}_session` : `multiple_${type}_sessions`;
    return t(key, { count: quantity });
  };

  const getDuration = (session) => {
    return parseInt(session.duration);
  };

  // Format price (1000 -> £1,000.00)
  const formatPrice = (price) => {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: "GBP",
    }).format(price);
  };

  // Date selection function
  const handleDateSelect = (date) => {
    setSelectedDate(date);
    setSelectedTime(null); // Reset time when date changes
  };

  // Time selection function
  const handleTimeSelect = (timeData) => {
    setSelectedTime(timeData.time);

    // Log selected availability ID and therapistId to console
    console.log("Selected availability information:", {
      availabilityId: timeData.availability._id,
      therapistId: therapist._id,
      startDate: timeData.availability.startDate,
      endDate: timeData.availability.endDate,
    });
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) return;
    setCouponLoading(true);
    try {
      const res = await axios.post(`${API_URL}/coupons/validate`, {
        code: couponCode.trim().toUpperCase(),
        therapistId: therapist._id,
      });
      if (res.data?.valid) {
        setCoupon(res.data);
        toast.success(`Coupon applied! ${res.data.discountPercent}% discount.`);
      } else {
        setCoupon(null);
        toast.error(res.data?.message || "Invalid coupon.");
      }
    } catch (err) {
      setCoupon(null);
      toast.error(err.response?.data?.message || "Invalid or expired coupon.");
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCoupon(null);
    setCouponCode("");
  };

  // Book appointment function with authentication check
  const handleBookAppointment = () => {
    // Check user authentication first
    const { authenticated, userType } = checkUserAuth();
    
    if (!authenticated) {
      // User is not logged in
      toast.error("You must sign in to book an appointment.", {
        position: "top-center",
        autoClose: 3000,
      });
      setTimeout(() => {
        navigate("/signIn");
      }, 1500);
      return;
    }
    
    if (userType !== 'user') {
      // User is logged in but not a regular user (maybe therapist or admin)
      toast.error("You do not have permission to book appointments.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }
    
    // User is authenticated and has correct permissions, proceed with booking
    const selectedPackage = sessionPackages.find(pkg => pkg._id === selectedSession);
    
    // If quantity is more than 1, don't check date/time
    if (selectedPackage && parseInt(selectedPackage.quantity) > 1) {
      if (selectedSession) {
        console.log("Multiple session appointment booked:", {
          session: selectedSession,
          package: selectedPackage,
        });
        // navigate("/userTherapyPlanner"); // Removed navigation
      } else {
        toast.error("Please select a session package.");
      }
    } else {
      // Check date/time for single session
      if (selectedSession && selectedDate && selectedTime) {
        // Find selected availability
        const selectedTimeData = availableTimes.find(
          (t) => t.time === selectedTime
        );
        if (selectedTimeData && selectedTimeData.availability && therapist) {
          const quantity = parseInt(selectedPackage?.quantity, 10) || 1;
          const totalPrice = getSessionPrice(quantity);
          const normalizedPrice = Number(totalPrice);

          const paymentData = {
            availabilityId: selectedTimeData.availability._id,
            therapistId: therapist._id,
            sessionPackageId: selectedPackage?._id,
            packageId: selectedPackage?._id,
            sessionType,
            duration: selectedPackage?.duration || 50,
            quantity,
            price: Number.isFinite(normalizedPrice) ? normalizedPrice : 0,
            startDate: selectedTimeData.availability.startDate,
            endDate: selectedTimeData.availability.endDate,
            userTimezone: USER_TIMEZONE,
          };

          if (selectedTimeData.availability.branchId) {
            paymentData.branchId = selectedTimeData.availability.branchId;
          }
          if (selectedPackage?.branchId && !paymentData.branchId) {
            paymentData.branchId = selectedPackage.branchId;
          }
          if (therapist?.therapistPayment?._id) {
            paymentData.therapistPaymentId = therapist.therapistPayment._id;
          }

          if (coupon?.valid) {
            paymentData.couponCode = couponCode.trim().toUpperCase();
          }

          // Cancel durumunda slot'u geri açabilmek için kaydet
          sessionStorage.setItem("pendingAvailabilityId", selectedTimeData.availability._id);
          sessionStorage.setItem("pendingTherapistId", therapist._id);

          // Call Redux action
          dispatch(createPaymentIntent(paymentData));
        } else {
          toast.error("There's an error with the selected appointment information. Please try again.");
        }
      } else {
        toast.error("Please select session, date and time.");
      }
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading therapist information...
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="flex justify-center items-center h-screen text-red-500">
        Error: {error}
      </div>
    );
  }

  // Therapist not found state
  if (!therapist) {
    return (
      <div className="flex justify-center items-center h-screen">
        Therapist not found
      </div>
    );
  }

  // Extract expertise areas and branches
  const expertiseTags = therapist.expertisesTags || [];
  const branches = therapist.branchIds || [];
  const certificates = therapist.certificates || [];

  // Calculate years of experience from experience array
  const getYearsOfExperience = () => {
    const experience = therapist.experience;
    if (!experience || experience.length === 0) return null;
    const earliest = experience.reduce((min, exp) => {
      const year = exp.start ? new Date(exp.start).getFullYear() : null;
      return year && year < min ? year : min;
    }, new Date().getFullYear());
    const years = new Date().getFullYear() - earliest;
    return years > 0 ? years : null;
  };
  const yearsOfExperience = getYearsOfExperience();

  // Get available times for selected date
  const availableTimes = getAvailableTimesForDate(selectedDate);

  // Selected package information
  const selectedPackage = sessionPackages.find(
    (pkg) => pkg._id === selectedSession
  );
  const isMultipleSession =
    selectedPackage && parseInt(selectedPackage.quantity) > 1;

  return (
    <>
      <div className="mx-auto max-w-7xl px-3 md:px-4 mt-[80px]">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          {/* Left section - Therapist Profile and Details */}
          <div className="flex-1 space-y-6">
            {/* Therapist Profile Card - Small */}
            <div className="bg-gradient-to-r from-tertiary to-tertiary rounded-[20px] p-6 shadow-sm border border-cards-border">
              <div className="flex items-center gap-4">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center text-white font-bold text-xl flex-shrink-0 overflow-hidden shadow-lg">
                  {therapist.picture ? (
                    <img
                      src={therapist.picture}
                      alt={`${therapist.name} ${therapist.surname}`}
                      className="w-full h-full object-cover rounded-full"
                    />
                  ) : (
                    `${therapist.name.charAt(0)}${therapist.surname.charAt(0)}`
                  )}
                </div>
                <div className="flex-1">
                  <h2 className="text-xl font-poppins font-bold text-text mb-1">
                    {therapist.name} {therapist.surname}
                  </h2>

                  {yearsOfExperience && (
                    <p className="text-sm font-poppins text-primary font-semibold mb-2">
                      {yearsOfExperience}+ {t("yearsOfExperience")}
                    </p>
                  )}

                  {/* Expertise Areas Tags */}
                  <div className="flex flex-wrap gap-2">
                    {expertiseTags.length > 0 ? (
                      expertiseTags.slice(0, 5).map((tag, index) => (
                        <span
                          key={index}
                          className="px-3 py-1 bg-tertiary text-primary rounded-[20px] text-xs font-poppins font-semibold"
                        >
                          {tag.name}
                        </span>
                      ))
                    ) : (
                      <div className="text-text font-poppins text-sm">
                        No expertise area information available
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Detail Information Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left Column - Education and Work Methods Stacked */}
              <div className="space-y-6">
                {/* Education */}
                <div className="bg-background rounded-[20px] p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-tertiary rounded-full flex items-center justify-center">
                      <span className="text-primary text-lg">🎓</span>
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-primary">
                      Education
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {therapist.education && therapist.education.length > 0 ? (
                      therapist.education.map((edu, index) => (
                        <div
                          key={index}
                          className="border-l-4 border-blue-500 pl-4 bg-cards-bg p-3 rounded-[10px]"
                        >
                          <p className="font-poppins font-bold text-base text-text">
                            {edu.school || "School information not available"}
                          </p>
                          <p className="font-poppins text-sm text-text">
                            {edu.finish 
                              ? `Graduated: ${edu.finish}`
                              : "Graduation date not available"
                            }
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-text font-poppins text-sm">
                          No education information available
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Work Methods */}
                <div className="bg-background rounded-[20px] p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-tertiary rounded-full flex items-center justify-center">
                      <span className="text-primary text-lg">🔧</span>
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-primary">
                      Work Methods
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {branches.length > 0 ? (
                      branches.map((branch, index) => (
                        <div
                          key={index}
                          className="bg-cards-bg p-3 rounded-[15px]"
                        >
                          <p className="font-poppins font-bold text-base text-text">
                            {branch.name}
                          </p>
                        </div>
                      ))
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-text font-poppins text-sm">
                          No work method information available
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Languages Section */}
                <div className="bg-background rounded-[20px] p-6 shadow-sm">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 bg-tertiary rounded-full flex items-center justify-center">
                      <span className="text-primary text-lg">🌐</span>
                    </div>
                    <h3 className="font-poppins font-bold text-lg text-primary">
                      Languages
                    </h3>
                  </div>
                  <div className="space-y-3">
                    {therapist.languages && therapist.languages.length > 0 ? (
                      [...therapist.languages]
                        .sort((a, b) => {
                          const getCodeLabel = (lang) => {
                            const langId = typeof lang === 'object' ? lang._id : lang;
                            const found = fetchedLanguages.find(l => l._id === langId);
                            const code = (found?.code || (typeof lang === 'object' ? lang.code : '') || '').toUpperCase();
                            const name = (found?.name || (typeof lang === 'object' ? lang.name : '') || '').toUpperCase();
                            return { code, name };
                          };
                          const labelA = getCodeLabel(a);
                          const labelB = getCodeLabel(b);
                          const isEnA = labelA.code === 'EN' || labelA.name === 'ENGLISH';
                          const isEnB = labelB.code === 'EN' || labelB.name === 'ENGLISH';
                          if (isEnA && !isEnB) return -1;
                          if (!isEnA && isEnB) return 1;
                          return 0;
                        })
                        .map((langData, index) => {
                          const langId = typeof langData === 'object' ? langData._id : langData;
                          const foundLang = fetchedLanguages.find(l => l._id === langId);
                          const displayName = foundLang ? foundLang.name : (typeof langData === 'object' ? langData.name : "Language");
                          
                          return (
                            <div
                              key={index}
                              className="bg-cards-bg p-3 rounded-[15px]"
                            >
                              <p className="font-poppins font-bold text-base text-text">
                                {displayName}
                              </p>
                            </div>
                          );
                        })
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-text font-poppins text-sm">
                          No language information available
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* Certificates */}
                {certificates.length > 0 && (
                  <div className="bg-background rounded-[20px] p-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="w-8 h-8 bg-tertiary rounded-full flex items-center justify-center">
                        <span className="text-primary text-lg">🏆</span>
                      </div>
                      <h3 className="font-poppins font-bold text-lg text-primary">
                        Certificates
                      </h3>
                    </div>
                    <div className="space-y-3">
                      {certificates.map((certificate, index) => (
                        <div
                          key={index}
                          className="bg-cards-bg p-3 rounded-[15px]"
                        >
                          <p className="font-poppins font-bold text-base text-text">
                            {certificate.name}
                          </p>
                          {certificate.description && (
                            <p className="font-poppins text-sm text-text mt-1">
                              {certificate.description}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Right Column - About */}
              <div className="bg-background rounded-[20px] p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-8 h-8 bg-tertiary rounded-full flex items-center justify-center">
                    <span className="text-primary text-lg">👤</span>
                  </div>
                  <h3 className="font-poppins font-bold text-lg text-primary">
                    About
                  </h3>
                </div>
                <div className="font-poppins text-sm text-text leading-relaxed bg-cards-bg p-4 rounded-[10px] break-words overflow-hidden">
                  {therapist.bio ? (
                    <div className="break-words" dangerouslySetInnerHTML={{ __html: therapist.bio }} />
                  ) : (
                    <div className="text-center py-4">
                      <div className="text-text font-poppins text-sm">
                        No about information available
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right section - Session Selection and Calendar (Compact) */}
          <div className="w-full lg:w-[400px] space-y-4">

            {/* Session Type Selector - only if therapist offers couples */}
            {therapist.coupleSessionPrice && (
              <div className="bg-tertiary rounded-[20px] p-4">
                <h3 className="font-poppins font-bold text-base mb-3 text-primary">
                  Session Type
                </h3>
                <div className="flex gap-2">
                  <button
                    onClick={() => setSessionType("individual")}
                    className={`flex-1 py-2 rounded-[12px] text-sm font-poppins font-semibold transition-all ${
                      sessionType === "individual"
                        ? "bg-button-bg text-white shadow"
                        : "bg-cards-bg text-text hover:bg-white"
                    }`}
                  >
                    Individual
                    <span className="block text-xs font-normal mt-0.5 opacity-80">
                      {formatPrice(therapist.hourlyPrice || 0)}
                    </span>
                  </button>
                  <button
                    onClick={() => setSessionType("couples")}
                    className={`flex-1 py-2 rounded-[12px] text-sm font-poppins font-semibold transition-all ${
                      sessionType === "couples"
                        ? "bg-button-bg text-white shadow"
                        : "bg-cards-bg text-text hover:bg-white"
                    }`}
                  >
                    Couples
                    <span className="block text-xs font-normal mt-0.5 opacity-80">
                      {formatPrice(therapist.coupleSessionPrice)}
                    </span>
                  </button>
                </div>
              </div>
            )}

            {/* Session Packages - Compact */}
            <div className="bg-tertiary rounded-[20px] p-4">
              <h3 className="font-poppins font-bold text-lg mb-3 text-primary">
                Session Packages
              </h3>
              {sessionPackages.length > 0 ? (
                <div className="space-y-2">
                  {sessionPackages.map((session) => (
                    <div
                      key={session._id}
                      className={`border rounded-[15px] p-3 cursor-pointer transition-all duration-300 ${
                        selectedSession === session._id
                          ? "border-primary bg-selectedbackground shadow-md"
                          : "border-cards-border bg-cards-bg hover:border-primary"
                      }`}
                      onClick={() => handleSessionSelect(session)}
                    >
                      <div className="flex items-start gap-2">
                        <div
                          className={`w-4 h-4 rounded-full border-2 mt-0.5 flex-shrink-0 ${
                            selectedSession === session._id
                              ? "border-primary bg-primary"
                              : "border-cards-border"
                          }`}
                        >
                          {selectedSession === session._id && (
                            <div className="w-full h-full rounded-full bg-cards-bg scale-50"></div>
                          )}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between mb-1">
                            <h4 className="font-poppins font-medium text-sm text-gray-800">
                              {getPackageName(session)}
                            </h4>
                            <p className="font-bold text-green-600">
                              {formatPrice(getSessionPrice(session.quantity))}
                            </p>
                          </div>
                          <span className="font-poppins text-xs text-gray-600">
                            {getDuration(session)} {t("minuteAbbr")} ({session.quantity}{" "}
                            sessions) - {session.appType}
                          </span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4">
                  <div className="text-text font-poppins text-sm">
                    No session package available
                  </div>
                </div>
              )}
            </div>

            {/* Calendar and Time Selection - Compact */}
            <div className="bg-background rounded-[20px] p-4">
              <h3 className="font-poppins font-bold text-lg mb-3 text-primary">
                Date and Time Selection
              </h3>

              {/* Show special message if multiple session package is selected */}
              {isMultipleSession ? (
                <div className="bg-cards-bg rounded-[15px] p-6 text-center">
                  <div className="mb-4">
                    <div className="w-16 h-16 mx-auto bg-tertiary rounded-full flex items-center justify-center mb-3">
                      <svg
                        className="w-8 h-8 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                    </div>
                    <p className="font-poppins text-sm text-text leading-relaxed">
                      For package purchases, you don't need to select a date
                      from the calendar. The counselor will contact you to
                      schedule your sessions.
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  {/* Calendar - Compact */}
                  <div className="bg-cards-bg rounded-[15px] p-3 mb-3">
                    {/* Calendar Header */}
                    <div className="flex items-center justify-between mb-2">
                      <button
                        onClick={previousMonth}
                        className="p-1 hover:bg-tertiary rounded transition-colors"
                      >
                        <svg
                          className="w-4 h-4 text-text"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M15 19l-7-7 7-7"
                          />
                        </svg>
                      </button>
                      <h4 className="font-poppins font-bold text-sm text-text">
                        {monthNames[currentMonth]} {currentYear}
                      </h4>
                      <button
                        onClick={nextMonth}
                        className="p-1 hover:bg-tertiary rounded transition-colors"
                      >
                        <svg
                          className="w-4 h-4 text-text"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5l7 7-7 7"
                          />
                        </svg>
                      </button>
                    </div>

                    {/* Day Names */}
                    <div className="grid grid-cols-7 gap-1 mb-1">
                      {dayNames.map((day) => (
                        <div key={day} className="text-center py-1 overflow-hidden">
                          <span className="font-poppins text-xs text-text font-medium hidden sm:inline">
                            {day}
                          </span>
                          <span className="font-poppins text-xs text-text font-medium sm:hidden">
                            {day[0]}
                          </span>
                        </div>
                      ))}
                    </div>

                    {/* Calendar Days */}
                    <div className="grid grid-cols-7 gap-1">
                      {calendarDays.map((date, index) => {
                        const isCurrentMonth = date.getMonth() === currentMonth;
                        const isToday =
                          date.toDateString() === new Date().toDateString();
                        const isSelected =
                          selectedDate &&
                          date.toDateString() === selectedDate.toDateString();
                        const isPastDate =
                          date < new Date().setHours(0, 0, 0, 0);
                        const hasAvailability = hasAvailabilityOnDate(date);
                        const isClickable =
                          isCurrentMonth && !isPastDate && hasAvailability;

                        return (
                          <div key={index} className="flex items-center justify-center">
                            <button
                              onClick={() =>
                                isClickable && handleDateSelect(date)
                              }
                              disabled={!isClickable}
                              className={`
                                w-full aspect-square max-w-[32px] rounded text-xs font-poppins transition-all duration-200 relative flex items-center justify-center
                                ${
                                  isSelected
                                    ? "bg-selectedbackground text-text font-bold"
                                    : isToday && isCurrentMonth && hasAvailability
                                    ? "bg-selectedbackground text-white"
                                    : isCurrentMonth && isClickable
                                    ? "hover:bg-selectedbackground hover:text-white text-text"
                                    : "text-text cursor-not-allowed"
                                }
                                ${
                                  hasAvailability && isCurrentMonth && !isPastDate
                                    ? "ring-1 ring-selectedbackground"
                                    : ""
                                }
                              `}
                            >
                              {date.getDate()}
                              {hasAvailability &&
                                isCurrentMonth &&
                                !isPastDate && (
                                  <div className="absolute bottom-0 right-0 w-1 h-1 bg-cards-border rounded-full"></div>
                                )}
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  {/* Time Selection - Compact */}
                  <div className="bg-cards-bg rounded-[15px] p-3">
                    <h4 className="font-poppins font-bold text-sm text-text mb-2">
                      Available Times
                    </h4>

                    {selectedDate ? (
                      availableTimes.length > 0 ? (
                        <div className="space-y-1 max-h-32 overflow-y-auto">
                          {availableTimes.map((timeData, index) => (
                            <button
                              key={index}
                              onClick={() => handleTimeSelect(timeData)}
                              className={`
                                w-full p-2 rounded-lg text-left font-poppins text-sm transition-all duration-200
                                ${
                                  selectedTime === timeData.time
                                    ? "bg-selectedbackground text-text"
                                    : "bg-tertiary text-text hover:bg-button-bg hover:text-white"
                                }
                              `}
                            >
                              🕐 {timeData.time}
                            </button>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-4">
                          <div className="text-text font-poppins text-sm">
                            No available times for this date
                          </div>
                        </div>
                      )
                    ) : (
                      <div className="text-center py-4">
                        <div className="text-text font-poppins text-sm">
                          Please select a date first
                        </div>
                      </div>
                    )}
                  </div>
                </>
              )}

              {/* Coupon Code */}
              <div className="mt-3">
                {coupon?.valid ? (
                  <div className="flex items-center justify-between bg-green-50 border border-green-200 rounded-[12px] px-3 py-2 text-sm">
                    <span className="text-green-700 font-medium">
                      {couponCode.toUpperCase()} — {coupon.discountPercent}% off
                    </span>
                    <button
                      onClick={removeCoupon}
                      className="text-green-500 hover:text-red-500 transition-colors ml-2 text-xs"
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                      onKeyDown={(e) => e.key === "Enter" && handleValidateCoupon()}
                      placeholder="Coupon code"
                      className="flex-1 px-3 py-1.5 text-sm border border-gray-200 rounded-[12px] focus:outline-none focus:ring-2 focus:ring-primary font-poppins"
                    />
                    <button
                      onClick={handleValidateCoupon}
                      disabled={!couponCode.trim() || couponLoading}
                      className="px-3 py-1.5 text-sm font-medium text-white bg-primary rounded-[12px] hover:bg-green-700 transition-colors disabled:opacity-50 whitespace-nowrap"
                    >
                      {couponLoading ? "..." : "Apply"}
                    </button>
                  </div>
                )}
              </div>

              {/* Book Appointment Button */}
              <div className="mt-3">
                {coupon?.valid && selectedSession && (
                  <div className="flex items-center justify-between text-sm mb-2 px-1">
                    <span className="text-gray-500 font-poppins">Total</span>
                    <span className="font-bold text-green-600 font-poppins">
                      {formatPrice(
                        getSessionPrice(parseInt(selectedPackage?.quantity, 10) || 1) *
                          (1 - (coupon.discountPercent || 0) / 100)
                      )}
                      <span className="text-gray-400 line-through text-xs ml-1">
                        {formatPrice(getSessionPrice(parseInt(selectedPackage?.quantity, 10) || 1))}
                      </span>
                    </span>
                  </div>
                )}
                <button
                  className={`w-full py-2 rounded-[25px] font-poppins font-bold text-white transition-all duration-300 text-sm ${
                    selectedSession && !paymentLoading
                      ? "bg-button-bg hover:bg-green-700 cursor-pointer"
                      : "bg-gray-400 cursor-not-allowed opacity-50"
                  }`}
                  onClick={handleBookAppointment}
                  disabled={!selectedSession || paymentLoading}
                >
                  {paymentLoading ? "Processing..." : "BOOK APPOINTMENT"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ExpertSlider */}
      <div className="mt-8">
        <ExpertSlider id={id} />
      </div>
    </>
  );
};

export default TherapistDetail;
